/**
 * Where a URL the app SHOWS somebody points (ui#1517). React-free by mandate (epic #26).
 *
 * **The rule, owner-set:** every URL the frontend displays is built from the browser's current
 * origin, never from a canonical constant. A recipient on `dev.transferify.ro` was being offered
 * `https://transferify.cloud/?ref=…` — the wrong brand and the wrong environment at once, because
 * the origin came from `seo/siteMeta.SITE_ORIGINS`, which is pinned to production and keyed on the
 * READER's language rather than on the host in front of them.
 *
 * Canonical identity is the deliberate opposite and stays where it is: `<link rel="canonical">`,
 * `og:url`, hreflang and the sitemaps must be identical for every visitor and every host, so they
 * keep reading `siteMeta`. The difference is who the URL is for. A canonical URL names the document
 * to a crawler; these URLs are handed to a person on a page, and a person can only act on the site
 * they are actually looking at.
 *
 * This module is the rule; `utils/useMountedOrigin` is the one-line React adapter that feeds it
 * `window.location.origin` behind the repo's hydration gate, so the first client render still
 * matches the pre-rendered HTML (the ui#432 pattern `nav/useSiteLinkOrigin` already uses).
 *
 * @see nav/siteLinkOrigin — the same question for a claimed/BYO host's nav links.
 */

/**
 * The browser's origin, normalized, or `null` when there is no host to speak of.
 *
 * `null` covers the two states that are not a browser: the SSG pre-render, and the first client
 * render before {@link ../utils/useMountedOrigin} has mounted. Callers render nothing rather than
 * guessing — a link or a wordmark naming a host we have not been told is exactly the defect above.
 */
export function displayedOrigin(currentOrigin: string | null | undefined): string | null {
	return parseOrigin(currentOrigin)?.origin ?? null;
}

/**
 * `currentOrigin` as a URL, or `null` when it is absent or unparseable.
 *
 * Unparseable is treated as absent rather than thrown: the input travels from `window.location`
 * through React state, and a page that threw on the way to rendering a footer line would take the
 * recipient's whole download card with it.
 */
function parseOrigin(currentOrigin: string | null | undefined): URL | null {
	if (!currentOrigin) {
		return null;
	}
	try {
		const url = new URL(currentOrigin);
		// Opaque origins (`data:`, `blob:` and friends) serialize to the literal string "null" and
		// name no host anyone could visit.
		return url.origin === "null" ? null : url;
	} catch {
		return null;
	}
}
